import React, { useState } from 'react';
import { Plus, Edit, FileText, ChevronDown, AlertTriangle, Sparkles } from 'lucide-react';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent, SensorDescriptor, SensorOptions } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { DraggablePageCard } from '../../components/DraggablePageCard';
import { cn } from '../../utils/cn';
import type { Page, Site } from '../../schemas/validation';
import type { PublishStatus } from '../../hooks/useUnpublishedSummary';

interface PagesSectionProps {
  pages: Page[];
  site?: Site | null;
  siteId: string;
  sensors: SensorDescriptor<SensorOptions>[];
  isReordering: boolean;
  activeId: string | null;
  activePage: Page | null;
  formatDate: (date: string) => string;
  onAddPageClick: () => void;
  onAddPageWithRoaryClick: () => void;
  /** When false, hides "Add page with Roary" entry points. */
  showRoaryActions?: boolean;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onEditPage: (page: Page) => void;
  onDeletePage: (page: Page) => void;
  onToggleHomePage: (page: Page) => void;
  onPublishPage?: (page: Page) => void;
  publishingPageId?: string | null;
  pageStatusMap?: Record<string, PublishStatus>;
}

export const PagesSection: React.FC<PagesSectionProps> = ({
  pages,
  siteId,
  sensors,
  isReordering,
  activeId,
  activePage,
  onAddPageClick,
  onAddPageWithRoaryClick,
  showRoaryActions = true,
  onDragStart,
  onDragEnd,
  onEditPage,
  onDeletePage,
  onToggleHomePage,
  onPublishPage,
  publishingPageId,
  pageStatusMap,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const hasExplicitHomePage = pages.some(p => p.isHomePage);
  const fallbackPage =
    !hasExplicitHomePage && pages.length > 0
      ? pages.find(p => p.title.toLowerCase() === 'home') ||
        pages.sort((a, b) => (a.orderPosition || 0) - (b.orderPosition || 0))[0]
      : null;

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center space-x-2 group"
        >
          <ChevronDown
            size={20}
            className={cn(
              'text-gray-500 transition-transform duration-200',
              isCollapsed && '-rotate-90'
            )}
          />
          <h2 className="text-lg font-medium text-gray-900 group-hover:text-gray-700">Pages</h2>
          <span className="text-sm text-gray-500">({pages.length})</span>
        </button>
        <div className="flex items-center space-x-2">
          {showRoaryActions && (
            <button
              onClick={onAddPageWithRoaryClick}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 font-medium transition-colors"
              title="Have Roary build a single page for you"
            >
              <Sparkles size={18} />
              <span>Add page with Roary</span>
            </button>
          )}
          <button
            onClick={onAddPageClick}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Add New Page</span>
          </button>
        </div>
      </div>

      {/* Home page banner */}
      {!isCollapsed && !hasExplicitHomePage && pages.length > 0 && fallbackPage && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">No home page set</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Visitors to your domain will see: <strong>{fallbackPage.title}</strong>.{' '}
              <button
                onClick={() => onToggleHomePage(fallbackPage)}
                className="underline hover:text-amber-900 font-medium"
              >
                Set as Home Page
              </button>
            </p>
          </div>
        </div>
      )}

      {!isCollapsed &&
        (pages.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pages yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first page.</p>
            <div className="mt-6 flex items-center justify-center space-x-2">
              {showRoaryActions && (
                <button
                  onClick={onAddPageWithRoaryClick}
                  className="btn-ghost text-orange-600 hover:text-orange-700 inline-flex items-center space-x-2"
                >
                  <Sparkles size={18} />
                  <span>Add page with Roary</span>
                </button>
              )}
              <button
                onClick={onAddPageClick}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add New Page</span>
              </button>
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={pages.map(page => page.id)} strategy={rectSortingStrategy}>
              <div
                className={cn(
                  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
                  isReordering && 'pointer-events-none opacity-75',
                  activeId && 'dragging'
                )}
              >
                {pages
                  .sort((a, b) => (a.orderPosition || 0) - (b.orderPosition || 0))
                  .map(page => (
                    <DraggablePageCard
                      key={page.id}
                      page={page}
                      siteId={siteId}
                      onEdit={onEditPage}
                      onDelete={onDeletePage}
                      onToggleHomePage={onToggleHomePage}
                      onPublish={onPublishPage}
                      isPublishing={publishingPageId === page.id}
                      backendPublishStatus={pageStatusMap?.[page.id]}
                    />
                  ))}
              </div>
            </SortableContext>

            <DragOverlay>
              {activePage ? (
                <div className="card w-64 max-w-[min(100vw-2rem,20rem)] min-w-0 opacity-95 shadow-2xl scale-105 rotate-2 border-primary-300">
                  <div className="p-4 min-w-0">
                    <h3
                      className="truncate text-sm font-semibold text-gray-900"
                      title={activePage.title}
                    >
                      {activePage.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">/{activePage.slug}</p>
                    <div className="mt-3 btn-primary w-full inline-flex items-center justify-center gap-1.5 py-1.5 text-xs">
                      <Edit size={13} />
                      Edit Page
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>

            {isReordering && (
              <div className="text-center py-4">
                <div className="inline-flex items-center space-x-2 text-primary-600">
                  <div className="w-4 h-4 spinner"></div>
                  <span className="text-sm font-medium">Saving new order...</span>
                </div>
              </div>
            )}
          </DndContext>
        ))}
    </div>
  );
};
